import React, { useState, useMemo, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Divider } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import "../../form/Form.js";
import "../../form/FormControlGroup.js";
import "../../form/FormLabel.js";
import { ToggleSwitch } from "../../form/ToggleSwitch.js";
import { Select } from "../../select/Select.js";
import "../../chip/ChipRenderer.js";
import "../../select/selectStyle.js";
import "../../select/MultiSelectFooter.js";
import "../../select/MultiSelect.js";
import "../../select/MultiSelectItem.js";
import "../../select/SelectArrowIcon.js";
import "../../select/VirtualizedMultiSelectOptionList.js";
import "../../select/SingleSelect.js";
import "../../select/MultiSelectWithExclude.js";
import "../../select/MultiSelectTextCounterWithTooltip.js";
import { secondsSinceEpoch } from "../../../utils/date.js";
import { DateRangeFilter } from "./DateRangeFilter.js";
import { getAvailableDateRange } from "./utils.js";
const localized = defineMessages({
  beforeValidationMessage: {
    id: "DateRangeHistoricalFilter_beforeValidationMessage",
    defaultMessage: "Please select a date range before {date}. To view recent {entities}, turn off the Historical Data switcher"
  },
  afterValidationMessage: {
    id: "DateRangeHistoricalFilter_afterValidationMessage",
    defaultMessage: "Please select a date range from {date}. Use the Historical Data for older period."
  },
  historicalLabel: {
    id: "DateRangeHistoricalFilter_historicalLabel",
    defaultMessage: "Use Historical Data"
  },
  year: {
    id: "DateRangeHistoricalFilter_yearLabel",
    defaultMessage: "Year"
  }
});
const useStyles = makeStyles()((theme) => ({
  headerContainer: {
    paddingTop: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5)
  },
  switch: {
    width: "100%",
    padding: 0
  },
  select: {
    paddingLeft: theme.spacing(2.5)
  }
}));
function DateRangeHistoricalFilter({
  value,
  onChange,
  rangeMode = "default",
  locale,
  width = "fixed",
  placeholder,
  entities,
  recentDateRange,
  historicalDateRange,
  hasInputTooltip,
  filterEntity
}) {
  const [isHistorical, setIsHistorical] = useState(!!value?.isHistorical);
  const options = useMemo(() => getOptions(), [historicalDateRange.from.toDateString(), historicalDateRange.to?.toDateString()]);
  const [year, setYear] = useState(value?.year ?? options[options.length - 1].value);
  const [selectedDateRange, setSelectedDateRange] = useState({ from: value?.from, to: value?.to });
  const { formatMessage } = useIntl();
  const { classes } = useStyles();
  useEffect(() => {
    if (value?.isHistorical !== void 0 && isHistorical !== value?.isHistorical) {
      setIsHistorical(value?.isHistorical);
    }
  }, [value.isHistorical]);
  useEffect(() => {
    if (value.year !== void 0 && year !== value?.year) {
      setYear(value?.year);
    }
  }, [value.year]);
  useEffect(() => {
    if (value?.from) {
      setSelectedDateRange({ from: value?.from, to: value?.to });
    }
  }, [value?.from, value?.to]);
  function getOptions() {
    const minYear = historicalDateRange.from.getUTCFullYear();
    const result = [];
    for (let i = minYear; i <= historicalDateRange.to.getUTCFullYear(); i++) {
      result.push({
        label: i.toString(),
        value: i
      });
    }
    return result;
  }
  function handleSelect(value2) {
    if (selectedDateRange?.from !== value2?.from || selectedDateRange?.to !== value2?.to) {
      setSelectedDateRange(value2);
    }
  }
  function handleChange(value2) {
    setSelectedDateRange(value2);
    onChange({
      ...value2,
      isHistorical,
      year: isHistorical ? year : void 0
    });
  }
  const availableDateRangeForSelectionUpdated = useMemo(
    () => getAvailableDateRange(isHistorical, year, historicalDateRange, recentDateRange),
    [
      isHistorical,
      year,
      recentDateRange.from.toDateString(),
      recentDateRange.to?.toDateString(),
      historicalDateRange.from.toDateString(),
      historicalDateRange?.to.toDateString()
    ]
  );
  function handleYearChange(value2) {
    setYear(value2[0]);
  }
  function handleHistoricalChange(value2) {
    setIsHistorical(value2);
    if (!value2) {
      handleSelect({
        from: secondsSinceEpoch(recentDateRange?.from),
        to: recentDateRange?.to ? secondsSinceEpoch(recentDateRange.to) : "now"
      });
    }
  }
  return /* @__PURE__ */ React.createElement(
    DateRangeFilter,
    {
      value: selectedDateRange,
      onChange: handleChange,
      onSelect: handleSelect,
      rangeMode,
      locale,
      availableDateRangeForSelection: availableDateRangeForSelectionUpdated,
      width,
      placeholder,
      filterEntity,
      hasInputTooltip,
      errorMessage: {
        afterNotValid: !isHistorical && {
          label: localized.afterValidationMessage,
          labelParams: {
            entities
          }
        },
        beforeNotValid: isHistorical && {
          label: localized.beforeValidationMessage,
          labelParams: {
            entities
          }
        }
      },
      footer: /* @__PURE__ */ React.createElement("div", { className: classes.headerContainer }, /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(
        ToggleSwitch,
        {
          value: isHistorical,
          onChange: handleHistoricalChange,
          fieldName: formatMessage(localized.historicalLabel),
          className: classes.switch,
          withVerticalPaddings: true
        }
      ), isHistorical ? /* @__PURE__ */ React.createElement(
        Select,
        {
          className: classes.select,
          label: localized.year,
          options,
          value: [year],
          onSubmit: handleYearChange
        }
      ) : null, /* @__PURE__ */ React.createElement(Divider, null))
    }
  );
}
export {
  DateRangeHistoricalFilter
};
//# sourceMappingURL=DateRangeHistoricalFilter.js.map
